import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Package, Clock, CheckCircle, Truck, XCircle, ChevronRight, ShoppingBag, ArrowLeft } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const OrderHistoryPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const { currentLanguage, t: tg } = useLanguage();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [API_URL, isAuthenticated, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString({
      en: 'en-US', es: 'es-ES', ru: 'ru-RU', de: 'de-DE',
      fr: 'fr-FR', it: 'it-IT', pl: 'pl-PL', nl: 'nl-NL', pt: 'pt-PT',
    }[currentLanguage] || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => `€${price.toFixed(2)}`;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'processing':
        return <Package className="w-4 h-4 text-blue-500" />;
      case 'shipped':
        return <Truck className="w-4 h-4 text-purple-500" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    const statusTranslations = {
      pending:    { en: 'Pending',    es: 'Pendiente',  ru: 'Ожидает',    de: 'Ausstehend', fr: 'En attente',    it: 'In attesa',    pl: 'Oczekujące', nl: 'In afwachting', pt: 'Pendente' },
      processing: { en: 'Processing', es: 'Procesando', ru: 'Обработка',  de: 'In Bearbeitung', fr: 'En traitement', it: 'In elaborazione', pl: 'Przetwarzanie', nl: 'In behandeling', pt: 'Em processamento' },
      shipped:    { en: 'Shipped',    es: 'Enviado',    ru: 'Отправлен',  de: 'Versendet',  fr: 'Expédié',       it: 'Spedito',      pl: 'Wysłane',    nl: 'Verzonden',     pt: 'Enviado' },
      delivered:  { en: 'Delivered',  es: 'Entregado',  ru: 'Доставлен',  de: 'Geliefert',  fr: 'Livré',         it: 'Consegnato',   pl: 'Dostarczone', nl: 'Bezorgd',      pt: 'Entregue' },
      cancelled:  { en: 'Cancelled',  es: 'Cancelado',  ru: 'Отменён',    de: 'Storniert',  fr: 'Annulé',        it: 'Annullato',    pl: 'Anulowane',  nl: 'Geannuleerd',   pt: 'Cancelado' }
    };
    return statusTranslations[status]?.[currentLanguage] || statusTranslations[status]?.en || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'shipped':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'delivered':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const translations = {
    en: {
      title: 'Order History',
      empty: 'No orders yet',
      emptyDesc: 'Your order history will appear here after you make a purchase',
      browseProducts: 'Browse Products',
      items: 'items',
      total: 'Total',
      orderDate: 'Order Date',
      backToProfile: 'Back to Profile'
    },
    es: {
      title: 'Historial de Pedidos',
      empty: 'Aún no hay pedidos',
      emptyDesc: 'Tu historial de pedidos aparecerá aquí después de realizar una compra',
      browseProducts: 'Ver Productos',
      items: 'artículos',
      total: 'Total',
      orderDate: 'Fecha del Pedido',
      backToProfile: 'Volver al Perfil'
    },
    ru: {
      title: 'История Заказов',
      empty: 'Пока нет заказов',
      emptyDesc: 'История заказов появится здесь после покупки',
      browseProducts: 'Смотреть Продукты',
      items: 'товаров',
      total: 'Итого',
      orderDate: 'Дата Заказа',
      backToProfile: 'Назад к профилю'
    },
    de: {
      title: 'Bestellverlauf',
      empty: 'Noch keine Bestellungen',
      emptyDesc: 'Ihr Bestellverlauf wird hier nach einem Kauf angezeigt',
      browseProducts: 'Produkte ansehen',
      items: 'Artikel',
      total: 'Gesamt',
      orderDate: 'Bestelldatum',
      backToProfile: 'Zurück zum Profil'
    },
    fr: {
      title: 'Historique des commandes',
      empty: 'Aucune commande pour le moment',
      emptyDesc: 'Votre historique de commandes apparaîtra ici après votre premier achat',
      browseProducts: 'Parcourir les produits',
      items: 'articles',
      total: 'Total',
      orderDate: 'Date de commande',
      backToProfile: 'Retour au profil'
    },
    it: {
      title: 'Storico ordini',
      empty: 'Ancora nessun ordine',
      emptyDesc: 'Lo storico dei tuoi ordini apparirà qui dopo aver effettuato un acquisto',
      browseProducts: 'Sfoglia i prodotti',
      items: 'articoli',
      total: 'Totale',
      orderDate: 'Data ordine',
      backToProfile: 'Torna al profilo'
    },
    pl: {
      title: 'Historia zamówień',
      empty: 'Brak zamówień',
      emptyDesc: 'Historia Twoich zamówień pojawi się tutaj po dokonaniu zakupu',
      browseProducts: 'Przeglądaj produkty',
      items: 'produktów',
      total: 'Razem',
      orderDate: 'Data zamówienia',
      backToProfile: 'Powrót do profilu'
    },
    nl: {
      title: 'Bestelgeschiedenis',
      empty: 'Nog geen bestellingen',
      emptyDesc: 'Uw bestelgeschiedenis verschijnt hier nadat u een aankoop heeft gedaan',
      browseProducts: 'Producten bekijken',
      items: 'artikelen',
      total: 'Totaal',
      orderDate: 'Besteldatum',
      backToProfile: 'Terug naar profiel'
    },
    pt: {
      title: 'Histórico de Encomendas',
      empty: 'Ainda sem encomendas',
      emptyDesc: 'O seu histórico de encomendas aparecerá aqui após realizar uma compra',
      browseProducts: 'Ver produtos',
      items: 'artigos',
      total: 'Total',
      orderDate: 'Data da encomenda',
      backToProfile: 'Voltar ao perfil'
    }
  };

  const t = translations[currentLanguage] || translations.en;

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-capsule-teal"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="ordersGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#ordersGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#ordersGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-10 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
              <Package className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{tg('orders_title')}</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.title}</h1>
            <p className="text-lg text-gray-300">{tg('orders_subtitle')}</p>
          </div>
        </div>
      </div>
      
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 -mt-8">
        <div className="max-w-4xl mx-auto">
          {/* Back to Profile Link */}
          <Link to="/profile" className="inline-flex items-center gap-2 text-capsule-teal hover:text-capsule-teal-dark mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">{t.backToProfile}</span>
          </Link>

          {orders.length === 0 ? (
            // Empty State
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardContent className="text-center py-12 sm:py-16">
                <div className="w-20 h-20 bg-capsule-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-10 h-10 text-capsule-teal" />
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t.empty}</h2>
                <p className="text-gray-600 mb-6 text-sm sm:text-base">{t.emptyDesc}</p>
                <Link to="/products">
                  <Button className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl">
                    {t.browseProducts}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            // Orders List
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="shadow-lg border-0 rounded-2xl overflow-hidden" data-testid={`order-${order.id}`}>
                  <CardHeader className="pb-2 px-4 sm:px-6 bg-gradient-to-r from-capsule-teal/5 to-capsule-aqua/5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span className="font-mono text-xs sm:text-sm text-gray-500">{order.id}</span>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${getStatusColor(order.status)}`}>
                          {getStatusIcon(order.status)}
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <span className="text-xs sm:text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="px-4 sm:px-6 py-4">
                    {/* Order Items */}
                    <div className="space-y-2 mb-4">
                      {order.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900 text-sm sm:text-base block truncate">
                              {item.product_name[currentLanguage] || item.product_name.en}
                            </span>
                          {item.dosage && (
                            <span className="ml-2 text-xs sm:text-sm text-gray-500">({item.dosage})</span>
                          )}
                          <span className="ml-2 text-xs sm:text-sm text-gray-500">x{item.quantity}</span>
                        </div>
                        <span className="font-medium text-gray-900 text-sm sm:text-base">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Order Total */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-500">
                      {order.items.reduce((sum, item) => sum + item.quantity, 0)} {t.items}
                    </span>
                    <div className="text-right">
                      <span className="text-xs sm:text-sm text-gray-500 mr-2">{t.total}:</span>
                      <span className="text-base sm:text-lg font-bold text-capsule-teal">
                        {formatPrice(order.total)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderHistoryPage;
